import React, { useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, UploadCloud, Search, X, Inbox, AlertTriangle, UserX, Info, LucideIcon } from 'lucide-react';
import { getSageInactiveEmployees, SageExportedInfo, SageInactiveEmployee } from '../../api/2020ph';

export type SageExportMode = 'excel' | 'api';

export interface SageExportItem {
  employeeID: number;
  altEmployeeID: string;
  name: { first: string; middle?: string; last: string };
  supervisorName: string;
}

interface SageExportSelectModalProps {
  mode: SageExportMode;
  items: SageExportItem[];
  week: number;
  dateRange: string;
  locationLabel?: string;
  /** Export in flight — locks the modal (confirm/cancel/close disabled) until it settles. */
  busy: boolean;
  /** `force` is true only after the "Export again" confirm step (api mode). */
  onConfirm: (employeeIDs: number[], force?: boolean) => void;
  onClose: () => void;
  /**
   * API mode only (TAS-180): each employee's latest Sage export this pay period. Those
   * employees start unchecked, wear an "Exported …" tag, and re-sending any of them goes
   * through an explicit confirm step. Never passed in Excel mode — absent, the modal
   * behaves exactly as before.
   */
  exported?: Map<number, SageExportedInfo>;
}

/** "10/3 2:14p" in the viewer's local time; '' when the timestamp does not parse. */
export function fmtSageExportTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${h % 12 === 0 ? 12 : h % 12}:${mm}${h < 12 ? 'a' : 'p'}`;
}

/**
 * "Exported 10/3 2:14p by Jane Doe · 42 rows · 38.50 hrs · exported 2 times" (tooltips + warning lists).
 * A backfilled record (the API's backfill script, rows recomputed after the fact) says so instead
 * of claiming a push time; a rows-unavailable backfill says why there are no rows.
 */
export function sageExportFacts(x: SageExportedInfo): string {
  const parts = x.backfilled
    ? [`Backfilled record (recomputed ${fmtSageExportTime(x.exportedAt).split(' ')[0]}), not the original push`]
    : [`Exported ${fmtSageExportTime(x.exportedAt)}${x.exportedBy ? ` by ${x.exportedBy}` : ''}`];
  if (x.rowsUnavailable) {
    parts.push(`rows could not be rebuilt — ${x.rowsUnavailableReason || 'reason not recorded'}`);
  } else if (x.noRows) parts.push('nothing was sent (no Sage rows)');
  else parts.push(`${x.rowCount} row${x.rowCount === 1 ? '' : 's'}`, `${Number(x.hours ?? 0).toFixed(2)} hrs`);
  if (x.exportCount > 1) parts.push(`exported ${x.exportCount} times`);
  if (!x.confirmed) parts.push('Sage’s answer was not received — the rows MAY be in Sage');
  return parts.join(' · ');
}

/** "Quit in Sage 10/15" / "Not found in Sage" — the row's sub-line flag. */
export function sageInactiveText(x: SageInactiveEmployee): string {
  if (x.sageStatus == null) return x.statusLabel;
  const d = x.inactiveDate ? x.inactiveDate.split('-') : null;
  return `${x.statusLabel} in Sage${d && d.length === 3 ? ` ${Number(d[1])}/${Number(d[2])}` : ''}`;
}

/** Sage employee-status check: loading, unavailable/failed, or the employees to warn about. */
type InactiveCheck =
  | { state: 'loading' }
  | { state: 'unchecked'; message: string }
  | { state: 'done'; byEmp: Map<number, SageInactiveEmployee> };

// Duplicated from TimesheetReviewBoardPage module scope (importing them back would be circular).
function initials(first: string, last: string): string {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}
function hashHue(s: string): number {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) & 0x7fffffff;
  return h % 360;
}

interface ModeMeta {
  icon: LucideIcon;
  title: string;
  verb: string;
  busyLabel: string;
  intro: string;
}

const MODES: Record<SageExportMode, ModeMeta> = {
  excel: {
    icon: FileSpreadsheet,
    title: 'Generate Sage Export Excel',
    verb: 'Generate Excel for',
    busyLabel: 'Generating…',
    intro: 'Choose which payroll-approved timesheets to include in the Sage export spreadsheet. All are selected by default — uncheck anyone you want to leave out.',
  },
  api: {
    icon: UploadCloud,
    title: 'Export to Sage',
    verb: 'Export',
    busyLabel: 'Exporting…',
    intro: 'Choose which payroll-approved timesheets to push to Sage. All are selected by default — uncheck anyone you want to hold back.',
  },
};

const SageExportSelectModal: React.FC<SageExportSelectModalProps> = ({
  mode, items, week, dateRange, locationLabel, busy, onConfirm, onClose, exported,
}) => {
  // Only api mode honours the export record; Excel mode never sees it.
  const exp = mode === 'api' ? exported : undefined;
  const hasRecord = !!exp && exp.size > 0;
  // Already-exported employees start unchecked — re-sending one is a deliberate act.
  const [sel, setSel] = useState<Set<number>>(
    () => new Set(items.filter((i) => !exp?.has(i.employeeID)).map((i) => i.employeeID)));
  const [q, setQ] = useState('');
  // The "Export again" confirm step (api mode, only when exported employees are selected).
  const [phase, setPhase] = useState<'select' | 'confirm'>('select');

  const m = MODES[mode];
  const ModeIcon = m.icon;

  // 2020PH (10/20): a timesheet can sit in Payroll Approved after the employee was separated in
  // Sage. Warn — never block; a final check for hours worked before the separation is legitimate.
  // Asked once per open, over every employee listed (not the filtered view).
  const [inactive, setInactive] = useState<InactiveCheck>({ state: 'loading' });
  useEffect(() => {
    let alive = true;
    const ids = items.map((i) => i.employeeID);
    if (!ids.length) { setInactive({ state: 'done', byEmp: new Map() }); return; }
    getSageInactiveEmployees(ids)
      .then((res) => {
        if (!alive) return;
        if (!res.checked) {
          setInactive({ state: 'unchecked', message: res.message || 'Sage employee status is not available here.' });
        } else {
          setInactive({ state: 'done', byEmp: new Map(res.employees.map((x) => [x.employeeID, x])) });
        }
      })
      .catch((err) => {
        console.warn('Sage employee status check failed; export unaffected.', err);
        if (alive) setInactive({ state: 'unchecked', message: 'Couldn’t check employee status in Sage.' });
      });
    return () => { alive = false; };
    // Items are fixed for the life of the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const inactiveByEmp = inactive.state === 'done' ? inactive.byEmp : null;

  const shown = useMemo(() => {
    const raw = q.trim().toLowerCase();
    if (!raw) return items;
    // "#123" and "123" both match the employee ID.
    const id = raw.startsWith('#') ? raw.slice(1) : raw;
    return items.filter((e) =>
      `${e.name.first} ${e.name.last}`.toLowerCase().includes(raw)
      || `${e.name.last}, ${e.name.first}`.toLowerCase().includes(raw)
      || (id !== '' && (e.altEmployeeID ?? '').toLowerCase().includes(id)));
  }, [items, q]);

  // Select-all works over the NOT-exported rows; ticking an exported one stays a separate act.
  // Without an export record this is every shown row, exactly as before.
  const selectable = useMemo(
    () => (exp ? shown.filter((e) => !exp.has(e.employeeID)) : shown),
    [shown, exp]);
  const shownSel = selectable.reduce((n, e) => n + (sel.has(e.employeeID) ? 1 : 0), 0);
  const allShownOn = selectable.length > 0 && shownSel === selectable.length;
  const someShown = shownSel > 0 && !allShownOn;
  const filtering = q.trim().length > 0;

  // Only a REAL prior export drives the force flow. A noRows marker sent nothing to Sage and
  // the API's guard ignores it, so ticking one stays a plain export.
  const selectedExported = useMemo(
    () => (exp ? items.filter((i) => {
      const x = exp.get(i.employeeID);
      return sel.has(i.employeeID) && !!x && !x.noRows;
    }) : []),
    [items, sel, exp]);
  const canForce = selectedExported.length > 0;

  const toggle = (id: number) => {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllShown = (on: boolean) => {
    setSel((prev) => {
      const next = new Set(prev);
      selectable.forEach((e) => { if (on) next.add(e.employeeID); else next.delete(e.employeeID); });
      return next;
    });
  };

  const close = () => { if (!busy) onClose(); };

  const selectedIDs = () => items.filter((i) => sel.has(i.employeeID)).map((i) => i.employeeID);

  // Mirrors SpectrumExportModal's canPush / canForce / openConfirm(force): a plain export
  // goes straight out; a selection holding exported employees has to pass the confirm step.
  const confirm = () => {
    if (busy || sel.size === 0) return;
    if (canForce) { setPhase('confirm'); return; }
    onConfirm(selectedIDs());
  };

  const confirmForce = () => {
    if (busy || sel.size === 0) return;
    onConfirm(selectedIDs(), true);
  };

  /** Row-end tag for an employee in the export record (api mode only). */
  const exportTag = (id: number) => {
    const x = exp?.get(id);
    if (!x) return null;
    if (x.noRows) {
      return <span className="vx-offtag" title={sageExportFacts(x)}>Nothing to export</span>;
    }
    if (!x.confirmed) {
      return (
        <span className="vx-offtag tr-sagetag warn" title={sageExportFacts(x)}>
          <AlertTriangle />Unconfirmed – check Sage
        </span>
      );
    }
    return (
      <span className="vx-offtag tr-sagetag ok" title={sageExportFacts(x)}>
        Exported {fmtSageExportTime(x.exportedAt)}
      </span>
    );
  };

  const exportedFactsList = (
    <ul className="tr-sageprior">
      {selectedExported.map((i) => {
        const x = exp?.get(i.employeeID);
        return x ? (
          <li key={i.employeeID}>
            <b>{i.name.first} {i.name.last}</b> <span>#{i.altEmployeeID}</span> — {sageExportFacts(x)}
          </li>
        ) : null;
      })}
    </ul>
  );
  const fixInSage = (
    <>
      Re-sending puts their hours in Sage again: delete them in Sage Daily Payroll if that batch
      is still open, otherwise correct with a Sage adjustment.
    </>
  );
  // One fixed-height slot whatever the state, so the list never moves when the answer arrives.
  const inactiveNote = (() => {
    if (inactive.state === 'loading') {
      return <div className="vx-note info tr-exp-sagest-note"><Info /><span>Checking employee status in Sage…</span></div>;
    }
    if (inactive.state === 'unchecked') {
      return <div className="vx-note info tr-exp-sagest-note"><Info /><span>{inactive.message} The export is not affected.</span></div>;
    }
    const n = inactive.byEmp.size;
    if (n === 0) {
      return <div className="vx-note info tr-exp-sagest-note"><Info /><span>Everyone listed is Current in Sage.</span></div>;
    }
    return (
      <div className="vx-note bad tr-exp-sagest-note">
        <UserX />
        <span>
          <b>{n} employee{n === 1 ? ' is' : 's are'} not Current in Sage</b> (quit, terminated, on leave…) — flagged
          below. Exporting still works; uncheck
          anyone who shouldn’t be paid in this batch.
        </span>
      </div>
    );
  })();

  const headMeta = (
    <div className="ms">Week {week} &middot; {dateRange}{locationLabel ? <> &middot; {locationLabel}</> : null}</div>
  );

  if (phase === 'confirm') {
    const n = selectedExported.length;
    return (
      <div className="trb-modal-bg" onClick={close}>
        <div className="trb-modal wide" onClick={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <span className="mi warn"><AlertTriangle /></span>
            <div>
              <h3>Export again to Sage?</h3>
              {headMeta}
            </div>
            <button className="x" onClick={close} type="button"><X /></button>
          </div>
          <div className="modal-body tr-exp vx-confirm">
            <p className="tr-exp-intro">
              Export {sel.size} employee{sel.size === 1 ? '' : 's'} to Sage, including{' '}
              <b>{n} already exported</b> this pay period?
            </p>
            <div className="vx-note warn">
              <AlertTriangle />
              <span>
                <b>{n} employee{n === 1 ? ' was' : 's were'} already exported to Sage.</b>{' '}
                {fixInSage}
                {exportedFactsList}
              </span>
            </div>
          </div>
          <div className="modal-foot tr-exp-foot">
            <button className="tr-btn" onClick={() => setPhase('select')} disabled={busy} type="button">Back</button>
            <span className="vx-grow" />
            <button className="tr-btn" onClick={close} disabled={busy} type="button">Cancel</button>
            <button className="tr-btn danger" onClick={confirmForce} disabled={busy || sel.size === 0} type="button">
              <UploadCloud /> {busy ? m.busyLabel : `Export again anyway (${sel.size})`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trb-modal-bg" onClick={close}>
      <div className="trb-modal wide tr-exp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="mi"><ModeIcon /></span>
          <div>
            <h3>{m.title}</h3>
            {headMeta}
          </div>
          <button className="x" onClick={close} type="button"><X /></button>
        </div>
        <div className="modal-body tr-exp">
          <p className="tr-exp-intro">
            {hasRecord
              ? 'Choose which payroll-approved timesheets to push to Sage. Everyone not yet exported this pay period is selected — already-exported employees start unchecked.'
              : m.intro}
          </p>
          {inactiveNote}
          <div className="tr-exp-search">
            <Search />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by employee name or ID…"
              autoComplete="off"
            />
          </div>
          <label className="tr-exp-all">
            <input
              type="checkbox"
              ref={(el) => { if (el) el.indeterminate = someShown; }}
              checked={allShownOn}
              onChange={(e) => toggleAllShown(e.target.checked)}
              disabled={selectable.length === 0}
            />
            <span className="tr-exp-all-lab">
              {hasRecord
                ? (filtering ? 'Select all shown not exported' : 'Select all not exported')
                : (filtering ? 'Select all shown' : 'Select all')}
            </span>
            <span className="tr-exp-count">
              <b>{sel.size}</b> of <b>{items.length}</b> selected{filtering ? <> &middot; <b>{shown.length}</b> shown</> : null}
            </span>
          </label>
          <div className="tr-exp-list">
            {shown.length > 0 ? shown.map((e) => {
              const hue = hashHue(`${e.name.first} ${e.name.last}`);
              const on = sel.has(e.employeeID);
              return (
                <label key={e.employeeID} className={`tr-exp-row${on ? ' on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(e.employeeID)} />
                  <span
                    className="tr-exp-av"
                    style={{ background: `hsl(${hue} 60% 92%)`, color: `hsl(${hue} 55% 38%)` }}
                  >
                    {initials(e.name.first, e.name.last)}
                  </span>
                  <span className="tr-exp-who">
                    <span className="tr-exp-nm">{e.name.first} {e.name.last}</span>
                    <span className="tr-exp-sub">
                      #{e.altEmployeeID} &middot; {e.supervisorName}
                      {inactiveByEmp?.has(e.employeeID) && (
                        <> &middot; <span className="tr-exp-sagest">{sageInactiveText(inactiveByEmp.get(e.employeeID)!)}</span></>
                      )}
                    </span>
                  </span>
                  {exportTag(e.employeeID)}
                </label>
              );
            }) : (
              <div className="tr-noresult">
                <Inbox />
                <p>No employee matches &ldquo;<b>{q.trim()}</b>&rdquo;.</p>
              </div>
            )}
          </div>
          {canForce && (
            <div className="vx-note warn">
              <AlertTriangle />
              <span>
                <b>
                  {selectedExported.length} selected employee{selectedExported.length === 1 ? ' was' : 's were'} already
                  exported to Sage this pay period.
                </b>{' '}
                {fixInSage}
                {exportedFactsList}
              </span>
            </div>
          )}
        </div>
        <div className="modal-foot tr-exp-foot">
          <button className="tr-btn" onClick={close} disabled={busy} type="button">Cancel</button>
          {canForce ? (
            <button className="tr-btn danger" onClick={confirm} disabled={busy || sel.size === 0} type="button">
              <ModeIcon /> {busy ? m.busyLabel : `Export again (${selectedExported.length} already exported)`}
            </button>
          ) : (
            <button className="tr-btn primary" onClick={confirm} disabled={busy || sel.size === 0} type="button">
              <ModeIcon /> {busy ? m.busyLabel : `${m.verb} ${sel.size}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SageExportSelectModal;
